import navigationData from '@/data/navigation.json';
import AuthenticationService from '@/services/AuthenticationService';
import { i18n } from '@/i18n';

export default {
  namespaced: true,

  state: {
    showSidebar: true, // APP_TARGET === "bmw", // false, //
    sidebarCollapsed: false,
    navigationSections: [],
    expandedSections: {},
    isDarkMode: false,
    showOffcanvas: false,
    showMobileSidebar: false,
    sidebarTheme: 'classic', // 'classic' oder 'skewed'
    debugMode: process.env.NODE_ENV === 'development'
  },

  getters: {
    showOffcanvas: state => state.showOffcanvas,
    showSidebar: state => state.showSidebar,
    sidebarCollapsed: state => state.sidebarCollapsed,
    sidebarWidth: state => state.sidebarCollapsed ? 90 : 288,
    navigationSections: state => state.navigationSections,
    expandedSections: state => state.expandedSections,
    isNavigationLoaded: state => state.navigationSections.length > 0,
    isDarkMode: state => state.isDarkMode,
    currentTheme: state => state.isDarkMode ? 'dark' : 'light',
    showMobileSidebar: state => state.showMobileSidebar,
    sidebarTheme: state => state.sidebarTheme,
    isSkewedTheme: state => state.sidebarTheme === 'skewed' || APP_TARGET === 'bmw',
    debugMode: state => state.debugMode
  },

  mutations: {
    SET_SHOW_SIDEBAR(state, value) {
      state.showSidebar = value;
    },

    TOGGLE_OFFCANVAS(state) {
      state.showOffcanvas = !state.showOffcanvas;
    },

    SET_SIDEBAR_COLLAPSED(state, value) {
      state.sidebarCollapsed = value;
    },

    TOGGLE_SIDEBAR_COLLAPSED(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },

    SET_NAVIGATION_SECTIONS(state, sections) {
      state.navigationSections = sections;
    },

    SET_EXPANDED_SECTIONS(state, expandedSections) {
      state.expandedSections = expandedSections;
    },

    TOGGLE_SECTION(state, sectionId) {
      state.expandedSections[sectionId] = !state.expandedSections[sectionId];
    },

    SET_DARK_MODE(state, value) {
      state.isDarkMode = value;
    },

    TOGGLE_DARK_MODE(state) {
      state.isDarkMode = !state.isDarkMode;
    },

    TOGGLE_MOBILE_SIDEBAR(state) {
      state.showMobileSidebar = !state.showMobileSidebar;
    },

    SET_MOBILE_SIDEBAR(state, value) {
      state.showMobileSidebar = value;
    },

    SET_SIDEBAR_THEME(state, theme) {
      state.sidebarTheme = theme;
    },

    SET_DEBUG_MODE(state, value) {
      state.debugMode = value;
    }
  },

  actions: {
    setShowSidebar({ commit }, value) {
      commit('SET_SHOW_SIDEBAR', value);
    },

    setSidebarCollapsed({ commit }, value) {
      commit('SET_SIDEBAR_COLLAPSED', value);
    },

    toggleSidebarCollapsed({ commit }) {
      commit('TOGGLE_SIDEBAR_COLLAPSED');
    },

    toggleShowOffcanvas({ commit }) {
      commit('TOGGLE_OFFCANVAS');
    },

    loadNavigation({ commit, state, rootGetters }) {
      // Nur laden, wenn noch nicht geladen
      if (state.navigationSections.length !== 0)
        return;
      let result = [];
      const user = AuthenticationService.localUserProfile;
      if (AuthenticationService.isMSMUser) {
        result = JSON.parse(JSON.stringify(navigationData.sections));
        for (let i = 0; i < result.length; i++) {
          const section = result[i];
          section.title = section.title?.includes('Webportal.') ? i18n.global.t(section.title) : section.title;

          for (let j = 0; j < section.items.length; j++) {
            const link = section.items[j];
            link.label = link.label?.includes('Webportal.') ? i18n.global.t(link.label) : link.label;
          }
        }
      } else if (!AuthenticationService.isMSMUser) {
        for (let i = 0; i < navigationData.sections.length; i++) {
          const section = navigationData.sections[i];

          if (!section.permission || section?.permission?.mode === "m") continue;

          const sec = {
            id: section.id,
            title: section.title?.includes('Webportal.') ? i18n.global.t(section.title) : section.title,
            expanded: section.expanded,
            permission: JSON.parse(JSON.stringify(section.permission)),
            items: []
          };
          result.push(sec);
          for (let j = 0; j < section.items.length; j++) {
            const link = section.items[j];
            if (link?.permission?.mode === "m") {
              continue;
            }

             if (!link.permission) {
                let item = {
                  id: link.id,
                  label:  link.label?.includes('Webportal.') ? i18n.global.t(link.label) : link.label,
                  route: link.route,
                  icon: link.icon,
                  disabled: link.disabled
                };
                sec.items.push(item);
                continue;
             }

            // Generic Permission Check
            if (link.permission.items && link.permission.items.length > 0) {
              const userPermissions = user.Permissions || [];
              const requiredPermissions = link.permission.items;
              const mode = link.permission.mode || 'any';

              const hasPermission = mode === 'all'
                ? requiredPermissions.every(p => userPermissions.includes(p))
                : requiredPermissions.some(p => userPermissions.includes(p));

              if (hasPermission) {
                let item = {
                  id: link.id,
                  label: link.label?.includes('Webportal.') ? i18n.global.t(link.label) : link.label,
                  route: link.route,
                  icon: link.icon,
                  disabled: link.disabled,
                  permission: JSON.parse(JSON.stringify(link.permission))
                };
                sec.items.push(item);
              }
            }
          }
        }
      }
      commit('SET_NAVIGATION_SECTIONS', result);

      // Initialisiere expandedSections basierend auf section.expanded
      const expandedSections = result.reduce((acc, section) => {
        acc[section.id] = section.expanded ?? true;
        return acc;
      }, {});
      commit('SET_EXPANDED_SECTIONS', expandedSections);
    },

    toggleSection({ commit }, sectionId) {
      commit('TOGGLE_SECTION', sectionId);
    },

    // Optional: Navigation zur Laufzeit aktualisieren
    updateNavigation({ commit }, sections) {
      commit('SET_NAVIGATION_SECTIONS', sections);
    },

    setDarkMode({ commit }, value) {
      commit('SET_DARK_MODE', value);
      // LocalStorage für Persistierung
      localStorage.setItem('theme', value ? 'dark' : 'light');
      // HTML-Klasse setzen
      document.documentElement.classList.toggle('dark', value);
    },

    toggleDarkMode({ commit, state }) {
      const newValue = !state.isDarkMode;
      commit('TOGGLE_DARK_MODE');
      // LocalStorage für Persistierung
      localStorage.setItem('theme', newValue ? 'dark' : 'light');
      // HTML-Klasse setzen
      document.documentElement.classList.toggle('dark', newValue);
      document.documentElement.setAttribute('data-bs-theme', newValue ? 'dark' : 'light');
    },

    initializeTheme({ commit }) {
      // Gespeichertes Theme aus localStorage laden
      const savedTheme = localStorage.getItem('theme');

      // Oder System-Präferenz prüfen
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = savedTheme === 'dark' || (!savedTheme && prefersDark);

      commit('SET_DARK_MODE', isDark);
      document.documentElement.classList.toggle('dark', isDark);
      document.documentElement.setAttribute('data-bs-theme', isDark ? 'dark' : 'light');
    },

    toggleMobileSidebar({ commit }) {
      commit('TOGGLE_MOBILE_SIDEBAR');
    },

    setMobileSidebar({ commit }, value) {
      commit('SET_MOBILE_SIDEBAR', value);
    },

    setSidebarTheme({ commit }, theme) {
      commit('SET_SIDEBAR_THEME', theme);
    },

    toggleDebugMode({ commit, state }) {
      commit('SET_DEBUG_MODE', !state.debugMode);
    }
  },
};
